import {none} from '@danwang/option';
import {
  useCreateCardBenefitMutation,
  type CreateCardBenefitMutation,
} from '@transactions/graphql';
import {fromSeconds} from 'common/date';
import * as React from 'react';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {Button} from 'src/Button';
import {CreditDestinationField} from 'src/CardBenefitsView/CreditDestinationField';
import {PeriodsEditor} from 'src/CardBenefitsView/PeriodsEditor';
import {
  ScheduleEditor,
  defaultScheduleState,
  explodeScheduleState,
} from 'src/CardBenefitsView/ScheduleEditor';
import CategoryPicker from 'src/CategoryPicker';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {SegmentedButton} from 'src/SegmentedButton';
import type {FetchResult} from '@apollo/client';
import type {Option} from '@danwang/option';
import type {PeriodRow} from 'src/CardBenefitsView/PeriodsEditor';
import type {ScheduleFormState} from 'src/CardBenefitsView/ScheduleEditor';
import type {SystemProps} from 'src/Modals';

export type CreateCardBenefitModalUserProps = {
  feePeriodId: string;
  feePeriodStartDate: number;
  feePeriodEndDate: number;
  /** The card the fee period belongs to — excluded from the credit picker. */
  cardAccountId: string;
  onSuccess: () => void;
};

type Mode = 'cadence' | 'custom';

export const CreateCardBenefitModal = ({
  onRequestClose,
  feePeriodId,
  feePeriodStartDate,
  feePeriodEndDate,
  cardAccountId,
  onSuccess,
}: CreateCardBenefitModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState<Option<string>>(none);
  const [mode, setMode] = React.useState<Mode>('cadence');
  const [schedule, setSchedule] =
    React.useState<ScheduleFormState>(defaultScheduleState);
  const [creditAccountId, setCreditAccountId] = React.useState<string | null>(
    null,
  );
  const [periods, setPeriods] = React.useState<PeriodRow[]>([
    {
      startDate: fromSeconds(feePeriodStartDate),
      // Editor rows show the inclusive last day; stored bounds are half-open
      endDate: fromSeconds(feePeriodEndDate).minus({days: 1}),
      amount: '',
    },
  ]);
  const [error, setError] = React.useState<string | null>(null);

  const [createBenefit, {loading}] = useCreateCardBenefitMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Either mode sends explicit period rows — a schedule is just a way
      // of generating them (the DB stores only the rows)
      const periodInputs =
        mode === 'cadence'
          ? explodeScheduleState(schedule, {
              startDate: feePeriodStartDate,
              endDate: feePeriodEndDate,
            })
          : periods.map((p) => {
              if (!p.startDate.isValid) throw new Error('Invalid start date');
              if (!p.endDate.isValid) throw new Error('Invalid end date');
              const cents = Math.round(parseFloat(p.amount) * 100);
              if (isNaN(cents)) throw new Error(`Invalid amount: ${p.amount}`);
              return {
                startDate: Math.floor(p.startDate.toSeconds()),
                // Editor rows are inclusive; stored bounds are half-open
                endDate: Math.floor(p.endDate.plus({days: 1}).toSeconds()),
                amount: cents,
              };
            });

      await createBenefit({
        variables: {
          feePeriodId,
          name: name.trim(),
          description: description.trim() || null,
          category: category.getOrElse(''),
          creditAccountId,
          periods: periodInputs,
        },
        update: (store, result: FetchResult<CreateCardBenefitMutation>) => {
          const newCategory = result?.data?.createCardBenefit?.category;
          newCategory && addNewCategory(store, newCategory);
        },
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const isValid =
    name.trim() &&
    !category.isEmpty &&
    (mode === 'cadence' ? schedule.amount.trim() !== '' : periods.length > 0);

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !isValid}
      >
        {loading ? 'Creating...' : 'Create'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create Benefit"
      footer={footer}
      size="lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="e.g., Uber credit, streaming credit"
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <TextInput
            value={description}
            onChange={setDescription}
            placeholder="Optional description"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Category</label>
          <CategoryPicker
            selectedCategory={category}
            onSelectCategory={setCategory}
          />
          <p className="text-muted-foreground text-xs">
            Prefilled when creating transactions for this benefit
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <SegmentedButton
            className="self-start"
            options={[
              {value: 'cadence', label: 'Regular schedule'},
              {value: 'custom', label: 'Custom periods'},
            ]}
            value={mode}
            onChange={setMode}
          />

          {mode === 'cadence' ? (
            <ScheduleEditor
              value={schedule}
              onChange={setSchedule}
              bounds={{startDate: feePeriodStartDate, endDate: feePeriodEndDate}}
            />
          ) : (
            <PeriodsEditor
              periods={periods}
              onChange={setPeriods}
              defaultCustomize={false}
              defaultGenStartDate={fromSeconds(feePeriodStartDate)}
              onError={setError}
            />
          )}
        </div>

        <CreditDestinationField
          value={creditAccountId}
          onChange={setCreditAccountId}
          cardAccountId={cardAccountId}
        />
      </form>
    </Modal>
  );
};
